import { JobOfferActivityType } from './job-offer-activity-type.enum'

/** Historical `activityType` strings that may still exist in `jobOffers.activityLog` JSON. */
const LEGACY_PROPOSAL_CREATED = new Set(['Offer Created', 'Offer Proposal Created'])
const LEGACY_PROPOSAL_UPDATED = new Set(['Offer Updated'])
/** Legacy: used both for per-step and final approval in older logs */
const LEGACY_APPROVAL_STEP_OR_COMPLETED = new Set(['Offer Approved'])
const LEGACY_CANDIDATE_REJECTED = new Set(['Offer Rejected', 'Offer rejected'])
const LEGACY_CANDIDATE_ACCEPTED = new Set(['Offer accepted'])
const LEGACY_APPROVAL_NEXT_LEVEL = new Set(['Sent to Next Level'])

export function isProposalCreatedType(t?: string | null): boolean {
    return t === JobOfferActivityType.PROPOSAL_CREATED || (t != null && LEGACY_PROPOSAL_CREATED.has(t))
}

export function isProposalUpdatedType(t?: string | null): boolean {
    return t === JobOfferActivityType.PROPOSAL_UPDATED || (t != null && LEGACY_PROPOSAL_UPDATED.has(t))
}

export function isStartDateUpdatedType(t?: string | null): boolean {
    return t === JobOfferActivityType.START_DATE_UPDATED
}

export function isApprovalCompletedType(t?: string | null): boolean {
    return t === JobOfferActivityType.APPROVAL_COMPLETED
}

/** True for final all-approvers-approved or per-step approval (legacy used same string for both). */
export function isApprovalStepOrCompletedType(t?: string | null): boolean {
    return (
        t === JobOfferActivityType.APPROVAL_COMPLETED ||
        t === JobOfferActivityType.APPROVAL_STEP_APPROVED ||
        (t != null && LEGACY_APPROVAL_STEP_OR_COMPLETED.has(t))
    )
}

export function isApprovalStepRejectedType(t?: string | null): boolean {
    return t === JobOfferActivityType.APPROVAL_STEP_REJECTED
}

export function isCandidateRejectedType(t?: string | null): boolean {
    return t === JobOfferActivityType.CANDIDATE_REJECTED || (t != null && LEGACY_CANDIDATE_REJECTED.has(t))
}

/** Approver or candidate rejection (for status UI). */
export function isAnyOfferRejectedType(t?: string | null): boolean {
    return isCandidateRejectedType(t) || isApprovalStepRejectedType(t)
}

export function isCandidateAcceptedType(t?: string | null): boolean {
    return t === JobOfferActivityType.CANDIDATE_ACCEPTED || (t != null && LEGACY_CANDIDATE_ACCEPTED.has(t))
}

export function isApprovalNextLevelType(t?: string | null): boolean {
    return t === JobOfferActivityType.APPROVAL_NEXT_LEVEL || (t != null && LEGACY_APPROVAL_NEXT_LEVEL.has(t))
}

export function activityLogHasOfferSent(activityLog: unknown): boolean {
    return Array.isArray(activityLog) && (activityLog as { activityType?: string }[]).some((a) => a?.activityType === JobOfferActivityType.OFFER_SENT)
}

export function activityLogHasSentForApproval(activityLog: unknown): boolean {
    return Array.isArray(activityLog) && (activityLog as { activityType?: string }[]).some((a) => a?.activityType === JobOfferActivityType.SENT_FOR_APPROVAL)
}

export type JobOfferActivityLogEntry = {
    activityType?: string
    createdAt?: string
    activity?: string
    performedByEmail?: string
}

function getLastMatchingEntry(activityLog: unknown, predicate: (t?: string) => boolean): JobOfferActivityLogEntry | undefined {
    if (!Array.isArray(activityLog)) return undefined
    const arr = (activityLog as JobOfferActivityLogEntry[]).filter((a) => predicate(a?.activityType))
    return arr.sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime())[0]
}

export function getLastOfferSentEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, (t) => t === JobOfferActivityType.OFFER_SENT)
}

export function getLastSentForApprovalEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, (t) => t === JobOfferActivityType.SENT_FOR_APPROVAL)
}

export function getLastProposalUpdatedEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, isProposalUpdatedType)
}

/** Latest revision that should allow re-sending to the candidate (full proposal edit or start-date-only). */
export function getLastOfferResendTriggerEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, (t) => isProposalUpdatedType(t) || isStartDateUpdatedType(t))
}

export function getLastRejectedEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, isAnyOfferRejectedType)
}

export function getLastCandidateAcceptedEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, isCandidateAcceptedType)
}

export function getLastCandidateRejectedEntry(activityLog: unknown): JobOfferActivityLogEntry | undefined {
    return getLastMatchingEntry(activityLog, isCandidateRejectedType)
}

/** For timeline icons in offer UI: approved / rejected / sent-like / neutral (edit). */
export function offerActivityIconKind(activityType?: string | null): 'approved' | 'rejected' | 'sent' | 'neutral' {
    if (!activityType) return 'neutral'
    if (isAnyOfferRejectedType(activityType)) return 'rejected'
    if (isApprovalStepOrCompletedType(activityType) || isCandidateAcceptedType(activityType)) return 'approved'
    const sentLike = new Set<string>([
        JobOfferActivityType.OFFER_SENT,
        JobOfferActivityType.REMINDER_SENT,
        JobOfferActivityType.SENT_FOR_APPROVAL,
        JobOfferActivityType.DOCUMENTS_REQUESTED,
        JobOfferActivityType.DOCUMENTS_UPLOADED,
        JobOfferActivityType.APPROVAL_NEXT_LEVEL,
        ...LEGACY_APPROVAL_NEXT_LEVEL,
    ])
    if (sentLike.has(activityType)) return 'sent'
    return 'neutral'
}
